/**
 * NotificationBell — Phase 17.2 #5.
 *
 * Topbar bell. Polls /api/notifications every 60 s, shows the actionable
 * count as a red badge, and on click drops down a list of urgent items
 * each linking to the relevant page.
 *
 * Categories rendered:
 *   - vouchers_missing (urgent — rose)
 *   - overdue_invoices (urgent — rose)
 *   - idle_leads       (warning — amber)
 *   - payment_received (info — slate, positive)
 *
 * Click on any row dismisses the dropdown and navigates. No mark-as-read
 * yet — the items disappear naturally as the underlying data changes
 * (voucher generated, invoice paid, lead contacted).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertTriangle, Receipt, UserMinus, Wallet, CalendarClock, ShieldCheck, PhoneCall } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_ICON = {
  vouchers_missing: Receipt,
  overdue_invoices: AlertTriangle,
  idle_leads:       UserMinus,
  payment_received: Wallet,
  docs_expiring:    CalendarClock,
  followups_due:    PhoneCall,
  tax_deadline:     ShieldCheck,
};

const KIND_TONE = {
  vouchers_missing: 'text-rose-700',
  overdue_invoices: 'text-rose-700',
  idle_leads:       'text-amber-700',
  payment_received: 'text-emerald-700',
  docs_expiring:    'text-amber-700',
  followups_due:    'text-amber-700',
  tax_deadline:     'text-rose-700',
};

// Hoisted out of NotificationBell's JSX on purpose. Keeping the member-expression
// lookups (KIND_ICON[...] / KIND_TONE[...]) inside an inline .map() block-body
// crashes the visual-edits Babel metadata plugin with a stack overflow.
const NotificationRow = ({ n, onNavigate }) => {
  const Icon = KIND_ICON[n.kind] || Bell;
  const tone = KIND_TONE[n.kind] || 'text-slate-500';
  return (
    <li>
      <Link
        to={n.href || '#'}
        onClick={onNavigate}
        data-testid={`notification-${n.id}`}
        className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
      >
        <span className={`shrink-0 mt-0.5 ${tone}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 truncate">{n.title}</div>
          <div className="text-[11px] text-slate-500 truncate">{n.subtitle}</div>
        </div>
      </Link>
    </li>
  );
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], actionable: 0, total: 0 });
  const dropdownRef = useRef(null);

  // Poll every 60 s. Cheap aggregate query — the backend caps at 20 rows.
  useEffect(() => {
    let alive = true;
    const fetchOnce = () => {
      axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true })
        .then((r) => { if (alive) setData(r.data || { items: [], actionable: 0 }); })
        .catch(() => { /* silent — bell is non-critical */ });
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Click-away to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const count = data.actionable || 0;
  // Hoist to a local identifier — mapping over a member expression (data.items.map)
  // triggers an infinite-recursion crash in the visual-edits Babel plugin.
  const items = data.items || [];
  const total = data.total || 0;

  // Opening the bell marks all currently-actionable notifications as read so
  // the red badge clears. It only reappears when a brand-new notification
  // arrives (the backend tracks read ids per user). Closing does nothing.
  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && (data.actionable || 0) > 0) {
        axios.post(`${BACKEND_URL}/api/notifications/read-all`, {}, { withCredentials: true })
          .then(() => setData((d) => ({ ...d, actionable: 0 })))
          .catch(() => { /* silent — non-critical */ });
      }
      return next;
    });
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell-wrap">
      <button
        type="button"
        onClick={handleToggle}
        data-testid="notification-bell"
        aria-label={`Notifications (${count})`}
        className="relative h-10 w-10 rounded-full inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-rose-600 text-white border-2 border-white"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[340px] bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-50"
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            <span className="text-[10px] text-slate-400 font-mono">{total} item{total !== 1 ? 's' : ''}</span>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">
                <Bell className="h-6 w-6 mx-auto text-slate-200 mb-2" />
                You&apos;re all caught up.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => (
                  <NotificationRow key={n.id} n={n} onNavigate={() => setOpen(false)} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
